/**
 * Resource configuration
 * Single source of truth for all loadable resources in the application
 */

// Resource type definitions
export type ResourceType = 'texture' | 'model' | 'hdr' | 'rQuery'

interface Resource {
  key: string;
  path: string;
  type: ResourceType;
}

// Flat resources object - easy to maintain, easy to reference
export const RESOURCES = {
  // Textures
  noise: { key: "noise", type: "texture", path: "./textures/noise/128x128/DomainWarp_02-128x128.png" },
  bottleNormal: { key: "bottleNormal", type: "texture", path: "./bottle/textures/bottle_normal.webp" },
  bottleRoughness: { key: "bottleRoughness", type: "texture", path: "./bottle/textures/bottle_roughness.webp" },
  label: { key: "label", type: "texture", path: "label" },

  // Spice textures (DataArrayTextures)
  spicesFallback: { key: "spicesFallback", type: "texture", path: "spices_texture_fallback" },
  spicesTexture: { key: "spicesTexture", type: "texture", path: "spices_texture_texture" },
  spicesNormal: { key: "spicesNormal", type: "texture", path: "spices_texture_normal" },

  // Models
  bottleModel: { key: "bottleModel", type: "model", path: "./bottle/bottle.glb" },

  // Environment
  environment: { key: "environment", type: "hdr", path: "./env/potsdamer_platz_1k.hdr" },

  // API
  nutrition: { key: "nutrition", type: "rQuery", path: "nutrition" },
} as const satisfies Record<string, Resource>;

// Helper: Get all resources as array
export const RESOURCES_ARRAY = Object.values(RESOURCES);

// Helper: Path to Key lookup map (O(1) for Three.js callbacks)
export const PATH_TO_KEY: Map<string, ResourceKey> = new Map(
  RESOURCES_ARRAY.map(r => [r.path, r.key])
);

// Helper: Key to Resource lookup map
export const KEY_TO_RESOURCE = new Map(
  RESOURCES_ARRAY.map(r => [r.key, r])
);

// Helper: React Query queryKey to Resource key mapping
// Automatically generated from RESOURCES - maps query names (paths) to resource keys
export const REACT_QUERY_KEY_TO_RESOURCE = Object.fromEntries(
  RESOURCES_ARRAY
    .filter(r => r.type === 'rQuery')
    .map(r => [r.path, r.key])
) satisfies Record<string, ResourceKey>;

// Type-safe resource keys
export type ResourceKey = typeof RESOURCES[keyof typeof RESOURCES]['key'];
